import * as React from "react";
import { cx } from "../internal/cx.js";

export type CardVariant = "default" | "section";

export interface CardProps extends React.HTMLAttributes<HTMLElement> {
  /**
   * Adds the elevated drop shadow. Use sparingly — only for cards that
   * float over a busy background. Inline cards inside a page section
   * should rely on the default low-weight elevation.
   */
  elevated?: boolean;
  /**
   * `section` emits the standard dashboard/wallet section-card chrome — a white
   * surface with the card shadow, meant to sit on the tinted body ground and
   * hold a `SectionHeader` + content. Cards nested inside a section card go
   * transparent automatically. Defaults to `default`.
   */
  variant?: CardVariant;
}

/**
 * Card root. Uses `<article>` so any inner `<header>` (`<CardHeader>`)
 * scopes to the card and does not register as a document banner. Without
 * this, docs pages composing multiple cards trip axe's
 * `landmark-no-duplicate-banner` rule.
 */
export const Card = React.forwardRef<HTMLElement, CardProps>(function Card(
  { className, elevated = false, variant = "default", ...props },
  ref,
) {
  return (
    <article
      ref={ref}
      className={cx(
        "cw-v2-card",
        elevated && "cw-v2-card--elevated",
        variant === "section" && "cw-v2-card--section",
        className,
      )}
      {...props}
    />
  );
});
Card.displayName = "Card";

export const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  function CardHeader({ className, ...props }, ref) {
    return <div ref={ref} className={cx("cw-v2-card__header", className)} {...props} />;
  },
);
CardHeader.displayName = "CardHeader";

export const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  function CardContent({ className, ...props }, ref) {
    return <div ref={ref} className={cx("cw-v2-card__content", className)} {...props} />;
  },
);
CardContent.displayName = "CardContent";

export const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  function CardFooter({ className, ...props }, ref) {
    return <div ref={ref} className={cx("cw-v2-card__footer", className)} {...props} />;
  },
);
CardFooter.displayName = "CardFooter";
